import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useTropicalDemoKind } from 'utils/featureFlags';
import { facilityAssetOrgActionName } from 'components/MapPage/CorporateViewTabs/NotificationsTab/facilityAssetChecklists';
import {
  getFacilityDemoPack,
  type FacilityDemoPack,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/facilityDemoPack';
import {
  keys as orgActionKeys,
  type GetOrgActionResponse,
} from 'components/IAP/hooks/orgActionHooks';

const ensureLocks = new Map<string, Promise<unknown>>();

function eventLevelDescription(pack: FacilityDemoPack): string {
  return `${pack.label} demo — event-level COA from the response Courses of Action.`;
}

function orgActionLabels(pack: FacilityDemoPack): string[] {
  return pack.responseChecklistItems
    .filter((item) => item.kind === 'org_action')
    .map((item) => item.label);
}

function expectedAssetActionNames(pack: FacilityDemoPack): {
  name: string;
  facilityId: string | null;
}[] {
  const labels = orgActionLabels(pack);
  const eventLevel = labels.map((label) => ({
    name: label,
    facilityId: null,
  }));
  const perAsset = pack.facilities.flatMap((facility) => {
    const assetLabel = pack.formatFacilityLabel(facility);
    return labels.map((label) => ({
      name: facilityAssetOrgActionName(label, assetLabel),
      facilityId: facility.id,
    }));
  });
  return [...eventLevel, ...perAsset];
}

async function ensureAssetOrgActionsForGroup({
  pack,
  groupGuid,
  userGuid,
  queryClient,
}: {
  pack: FacilityDemoPack;
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<GetOrgActionResponse[]>(
    `${config.apiGateway.ics_org_actions}/${groupGuid}`
  );
  const existingNames = new Set(
    (resp.data ?? [])
      .filter((action) => !action.archived)
      .map((action) => action.name)
  );

  const missing = expectedAssetActionNames(pack).filter(
    (entry) => !existingNames.has(entry.name)
  );
  if (missing.length === 0) {
    return;
  }

  await Promise.all(
    missing.map(({ name, facilityId }) =>
      axios.post(`${config.apiGateway.ics_org_actions}/${groupGuid}`, {
        name,
        description: facilityId
          ? pack.assetOrgActionDescription(facilityId)
          : eventLevelDescription(pack),
        checklist: [],
        status: 'Planned',
        user_guid: userGuid,
        utc_offset: -new Date().getTimezoneOffset(),
      })
    )
  );

  await queryClient.invalidateQueries({
    queryKey: orgActionKeys.byGroupAndUser(groupGuid, userGuid),
  });
}

/**
 * Seeds event-level and per-asset Courses of Action for whichever facility demo
 * is on.
 */
export function useEnsureFacilityDemoAssetOrgActions() {
  const pack = getFacilityDemoPack(useTropicalDemoKind());
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pack || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${pack.kind}:${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureAssetOrgActionsForGroup({
        pack,
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            `[${pack.label}] Failed to ensure per-asset Courses of Action org actions`,
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [pack, groupGuid, userGuid, queryClient]);
}
